import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  ArrowLeft,
  BringToFront,
  Download,
  FolderPlus,
  ImagePlus,
  PackageSearch,
  Redo2,
  ScanLine,
  SendToBack,
  Trash2,
  Undo2,
} from "lucide-react";
import client, { absoluteUrl } from "../api/client";
import { AddToProjectDialog, LengthInput, ProductPicker, UnitToggle, downloadFile, productName, useUnits } from "./common";
import { ART_CENTRE_MM } from "./quantities";
import { formatSize } from "./units";
import { DEFAULT_CORNERS, apply, homography, matrix3d, quadIsUsable } from "./wallMath";
import "../Moodboard/Moodboard.css";
import "./Architect.css";

// The flat wall is laid out on an element this many pixels wide, then mapped
// onto the photo; everything on it is placed in percent of the wall.
const PLANE = 1000;
// As SHADOW_* in api/visualiser.py.
const SHADOW = { offset: 10, blur: 18, darkness: 0.35 };
const MODES = [
  { key: "actual", label: "Actual size" },
  { key: "fill", label: "Fill wall" },
  { key: "tile", label: "Repeat pattern" },
];
const MIN_MM = 50;
const SAVE_DELAY = 700;
const HISTORY_LIMIT = 60;
const STATUS = { saved: "All changes saved", saving: "Saving...", error: "Not saved", conflict: "Changed elsewhere" };

const uid = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `p${Date.now()}${Math.random().toString(16).slice(2)}`);
const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const isTyping = (target) => target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);

const docFrom = (vis) => ({
  title: vis.title,
  project_id: vis.project_id,
  corners: vis.corners,
  wall_w_mm: vis.wall_w_mm,
  wall_h_mm: vis.wall_h_mm,
  lighting: vis.lighting,
  placements: vis.placements || [],
});

// A new product on the wall, at its real size where we know it.
function newPlacement(asset, wallW, wallH) {
  const details = asset.details || {};
  const aspect = asset.width && asset.height ? asset.height / asset.width : 1;
  if (details.sold_as === "area" || details.sold_as === "panel") return { id: uid(), file_id: asset.file_id, mode: "fill", x_mm: 0, y_mm: 0, w_mm: wallW, h_mm: wallH, tile_mm: 600, shadow: false };
  if (details.sold_as === "roll") {
    return { id: uid(), file_id: asset.file_id, mode: "tile", x_mm: 0, y_mm: 0, w_mm: wallW, h_mm: wallH, tile_mm: details.width_mm || 530, shadow: false };
  }
  let w = details.width_mm && details.height_mm ? details.width_mm : Math.min(900, Math.round(wallW * 0.4));
  let h = details.width_mm && details.height_mm ? details.height_mm : Math.round(w * aspect);
  const fit = Math.min(1, (wallW * 0.9) / w, (wallH * 0.9) / h);
  w = Math.round(w * fit);
  h = Math.round(h * fit);
  return {
    id: uid(),
    file_id: asset.file_id,
    mode: "actual",
    x_mm: Math.round((wallW - w) / 2),
    // Centred at eye level when the wall is tall enough.
    y_mm: Math.round(clamp(wallH - ART_CENTRE_MM - h / 2, 0, Math.max(0, wallH - h))),
    w_mm: w,
    h_mm: h,
    tile_mm: 600,
    shadow: true,
  };
}

function WallPlane({ doc, assets, quad, selectedId, interactive, onItemPointerDown, onResizePointerDown, grid }) {
  const { wall_w_mm: wallW, wall_h_mm: wallH } = doc;
  const height = (PLANE * wallH) / wallW;
  const transform = matrix3d(homography([[0, 0], [PLANE, 0], [PLANE, height], [0, height]], quad));
  const covers = doc.placements.filter((entry) => entry.mode !== "actual" && assets[entry.file_id]);
  const arts = doc.placements.filter((entry) => entry.mode === "actual" && assets[entry.file_id]);
  const px = (mm) => (mm * PLANE) / wallW;
  const plane = { width: PLANE, height, transform };
  const coverStyle = (entry) => ({
    backgroundImage: `url("${absoluteUrl(assets[entry.file_id].url)}")`,
    ...(entry.mode === "fill"
      ? { backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }
      : { backgroundSize: `${(entry.tile_mm / wallW) * 100}% auto`, backgroundPosition: "0 0", backgroundRepeat: "repeat" }),
  });
  const light = 1 - (doc.lighting ?? 60) / 100;

  return (
    <>
      {covers.length > 0 && (
        // Multiplied over the photo so the room's light and shade show through.
        <div className="ar-plane is-multiply" style={plane} aria-hidden="true">
          {covers.map((entry) => (
            <div key={entry.id} className="ar-cover" style={coverStyle(entry)} />
          ))}
        </div>
      )}
      <div className="ar-plane" style={plane}>
        {covers.length > 0 && (
          <div className="ar-cover-group" style={{ opacity: light }}>
            {covers.map((entry) => (
              <div key={entry.id} className="ar-cover" style={coverStyle(entry)} />
            ))}
          </div>
        )}
        {grid && <div className="ar-grid-overlay" style={{ backgroundSize: `${(grid / wallW) * 100}% ${(grid / wallH) * 100}%` }} />}
        {arts.map((entry) => (
          <div
            key={entry.id}
            className={`ar-art${entry.id === selectedId ? " is-selected" : ""}${interactive ? " is-interactive" : ""}`}
            style={{
              left: `${(entry.x_mm / wallW) * 100}%`,
              top: `${(entry.y_mm / wallH) * 100}%`,
              width: `${(entry.w_mm / wallW) * 100}%`,
              height: `${(entry.h_mm / wallH) * 100}%`,
            }}
            onPointerDown={interactive ? (event) => onItemPointerDown(event, entry) : undefined}
          >
            <img
              src={absoluteUrl(assets[entry.file_id].url) || undefined}
              alt=""
              draggable={false}
              style={{
                filter: entry.shadow
                  ? `drop-shadow(0 ${px(SHADOW.offset)}px ${px(SHADOW.blur)}px rgba(0, 0, 0, ${SHADOW.darkness}))`
                  : undefined,
              }}
            />
            {interactive && entry.id === selectedId && (
              <span className="ar-art-resize" onPointerDown={(event) => onResizePointerDown(event, entry)} title="Drag to resize" />
            )}
          </div>
        ))}
      </div>
    </>
  );
}

export default function WallEditor() {
  const { visId } = useParams();
  const [units, setUnits] = useUnits();
  const [doc, setDoc] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [assets, setAssets] = useState({});
  const [status, setStatus] = useState("saved");
  const [conflict, setConflict] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("place");
  const [selectedId, setSelectedId] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [projects, setProjects] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [box, setBox] = useState(null);
  const [historyFlags, setHistoryFlags] = useState({ undo: false, redo: false });

  const docRef = useRef(null);
  const version = useRef(1);
  const saveTimer = useRef(null);
  const saving = useRef(false);
  const pending = useRef(false);
  const history = useRef({ past: [], future: [] });
  const stageRef = useRef(null);
  const boxRef = useRef(null);
  const gesture = useRef(null);
  const fileRef = useRef(null);

  // --- loading and saving ------------------------------------------------------

  const adopt = useCallback((vis) => {
    version.current = vis.version;
    setPhoto(vis.photo);
    setAssets((current) => ({ ...current, ...vis.assets }));
  }, []);

  useEffect(() => {
    client
      .get(`/api/visualisations/${visId}`)
      .then(({ data }) => {
        adopt(data);
        const next = docFrom(data);
        docRef.current = next;
        setDoc(next);
        if (data.photo && !(data.corners && data.wall_w_mm && data.wall_h_mm)) setMode("mark");
      })
      .catch((err) => setError(err.response?.status === 404 ? "This wall was not found." : "Could not load the wall."));
    client
      .get("/api/projects")
      .then(({ data }) => setProjects(data.items || []))
      .catch(() => {});
  }, [visId, adopt]);

  const save = useCallback(async () => {
    if (saving.current) {
      pending.current = true;
      return;
    }
    const current = docRef.current;
    if (!current) return;
    saving.current = true;
    setStatus("saving");
    const body = { ...current, version: version.current };
    // Unfinished corners stay on screen and are saved once they make a wall.
    if (!current.corners || !quadIsUsable(current.corners)) delete body.corners;
    try {
      const { data } = await client.put(`/api/visualisations/${visId}`, body);
      adopt(data);
      setStatus(pending.current ? "saving" : "saved");
    } catch (err) {
      if (err.response?.status === 409) {
        setConflict(err.response.data.latest);
        setStatus("conflict");
      } else {
        setStatus("error");
        toast.error(err.response?.data?.error || "Could not save the wall.", { toastId: "wall-save" });
      }
      pending.current = false;
    } finally {
      saving.current = false;
      if (pending.current) {
        pending.current = false;
        save();
      }
    }
  }, [visId, adopt]);

  const change = useCallback(
    (next, { record = true } = {}) => {
      if (record) {
        const h = history.current;
        h.past.push(docRef.current);
        if (h.past.length > HISTORY_LIMIT) h.past.shift();
        h.future = [];
        setHistoryFlags({ undo: true, redo: false });
      }
      docRef.current = next;
      setDoc(next);
      if (status !== "conflict") {
        clearTimeout(saveTimer.current);
        setStatus("saving");
        saveTimer.current = setTimeout(save, SAVE_DELAY);
      }
    },
    [save, status]
  );

  const patch = (fields, options) => change({ ...docRef.current, ...fields }, options);
  const patchPlacement = (id, fields, options) =>
    patch({ placements: docRef.current.placements.map((entry) => (entry.id === id ? { ...entry, ...fields } : entry)) }, options);

  const travel = (from, to) => {
    const h = history.current;
    if (!h[from].length) return;
    h[to].push(docRef.current);
    const next = h[from].pop();
    setHistoryFlags({ undo: h.past.length > 0, redo: h.future.length > 0 });
    change(next, { record: false });
  };

  useEffect(() => {
    const warn = (event) => {
      if (status === "saving" || status === "error") {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [status]);

  const flush = async () => {
    clearTimeout(saveTimer.current);
    await save();
  };

  const resolveConflict = (keepMine) => {
    const latest = conflict;
    setConflict(null);
    version.current = latest.version;
    if (keepMine) {
      setStatus("saving");
      save();
    } else {
      adopt(latest);
      const next = docFrom(latest);
      docRef.current = next;
      setDoc(next);
      history.current = { past: [], future: [] };
      setHistoryFlags({ undo: false, redo: false });
      setStatus("saved");
    }
  };

  // --- photo and layout ------------------------------------------------------------

  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node || !photo) return undefined;
    const measure = () => {
      const { width, height } = node.getBoundingClientRect();
      const aspect = photo.width / photo.height;
      const w = Math.max(1, Math.min(width, height * aspect));
      setBox({ w, h: w / aspect });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [photo]);

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: upload } = await client.post("/api/uploads", form);
      await flush();
      const { data } = await client.put(`/api/visualisations/${visId}`, { version: version.current, photo_upload_id: upload.id });
      adopt(data);
      const next = { ...docFrom(data), corners: DEFAULT_CORNERS };
      docRef.current = next;
      setDoc(next);
      setMode("mark");
      setStatus("saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not use that photo.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // --- geometry ---------------------------------------------------------------------

  const corners = doc?.corners || DEFAULT_CORNERS;
  const quad = box ? corners.map(([x, y]) => [x * box.w, y * box.h]) : null;
  const wallReady = Boolean(doc?.wall_w_mm && doc?.wall_h_mm);
  const usable = quadIsUsable(corners);
  const toWall = useMemo(() => {
    if (!quad || !wallReady || !usable) return null;
    const height = (PLANE * doc.wall_h_mm) / doc.wall_w_mm;
    const map = homography(quad, [[0, 0], [PLANE, 0], [PLANE, height], [0, height]]);
    return (clientX, clientY) => {
      const rect = boxRef.current.getBoundingClientRect();
      const [px, py] = apply(map, [clientX - rect.left, clientY - rect.top]);
      return [(px / PLANE) * doc.wall_w_mm, (py / height) * doc.wall_h_mm];
    };
  }, [quad, wallReady, usable, doc?.wall_w_mm, doc?.wall_h_mm]);

  // --- pointer gestures --------------------------------------------------------------

  const startCorner = (event, index) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = { kind: "corner", index, before: docRef.current };
  };

  const startMove = (event, entry) => {
    if (event.button !== 0 || !toWall) return;
    event.preventDefault();
    event.stopPropagation();
    setSelectedId(entry.id);
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = { kind: "move", id: entry.id, from: toWall(event.clientX, event.clientY), origin: entry, before: docRef.current, moved: false };
  };

  const startResize = (event, entry) => {
    if (event.button !== 0 || !toWall) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture.current = { kind: "resize", id: entry.id, origin: entry, before: docRef.current, moved: false };
  };

  const onPointerMove = (event) => {
    const g = gesture.current;
    if (!g) return;
    if (g.kind === "corner") {
      const rect = boxRef.current.getBoundingClientRect();
      const point = [clamp((event.clientX - rect.left) / rect.width, 0, 1), clamp((event.clientY - rect.top) / rect.height, 0, 1)].map(
        (value) => Math.round(value * 100000) / 100000
      );
      const next = docRef.current.corners ? [...docRef.current.corners] : [...DEFAULT_CORNERS];
      next[g.index] = point;
      g.moved = true;
      patch({ corners: next }, { record: false });
      return;
    }
    const [mx, my] = toWall(event.clientX, event.clientY);
    // Near the photo's vanishing point the wall position runs off to infinity.
    if (!Number.isFinite(mx) || !Number.isFinite(my)) return;
    const o = g.origin;
    const { wall_w_mm: wallW, wall_h_mm: wallH } = docRef.current;
    g.moved = true;
    if (g.kind === "move") {
      // Always at least touching the wall, which also keeps it within what the API accepts.
      patchPlacement(
        g.id,
        {
          x_mm: Math.round(clamp(o.x_mm + mx - g.from[0], -o.w_mm, wallW)),
          y_mm: Math.round(clamp(o.y_mm + my - g.from[1], -o.h_mm, wallH)),
        },
        { record: false }
      );
    } else {
      const ratio = o.h_mm / o.w_mm;
      const largest = 2 * Math.max(wallW, wallH);
      const w = clamp(Math.max(Math.round(mx - o.x_mm), Math.round((my - o.y_mm) / ratio)), MIN_MM, largest / Math.max(1, ratio));
      patchPlacement(g.id, { w_mm: Math.round(w), h_mm: Math.max(MIN_MM, Math.round(w * ratio)) }, { record: false });
    }
  };

  const endGesture = () => {
    const g = gesture.current;
    gesture.current = null;
    if (g?.moved) {
      const h = history.current;
      h.past.push(g.before);
      if (h.past.length > HISTORY_LIMIT) h.past.shift();
      h.future = [];
      setHistoryFlags({ undo: true, redo: false });
    }
  };

  // --- placements ---------------------------------------------------------------------

  const addProducts = async ([item]) => {
    setDialog(null);
    try {
      const { data: details } = await client.get(`/api/products/${item.file_id}/details`);
      const asset = { file_id: item.file_id, url: item.thumbnail_path, width: item.width, height: item.height, name: item.file_name, details };
      setAssets((current) => ({ ...current, [item.file_id]: current[item.file_id] || asset }));
      const entry = newPlacement(asset, docRef.current.wall_w_mm, docRef.current.wall_h_mm);
      patch({ placements: [...docRef.current.placements, entry] });
      setSelectedId(entry.id);
    } catch {
      toast.error("Could not add that product.");
    }
  };

  const removePlacement = (id) => {
    patch({ placements: docRef.current.placements.filter((entry) => entry.id !== id) });
    setSelectedId(null);
  };

  const reorder = (id, toFront) => {
    const rest = docRef.current.placements.filter((entry) => entry.id !== id);
    const moving = docRef.current.placements.find((entry) => entry.id === id);
    patch({ placements: toFront ? [...rest, moving] : [moving, ...rest] });
  };

  useEffect(() => {
    const onKey = (event) => {
      if (!docRef.current || dialog || isTyping(event.target)) return;
      const mod = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();
      if (mod && key === "z") {
        event.preventDefault();
        event.shiftKey ? travel("future", "past") : travel("past", "future");
      } else if (mod && key === "y") {
        event.preventDefault();
        travel("future", "past");
      } else if (selectedId && (key === "delete" || key === "backspace")) {
        event.preventDefault();
        removePlacement(selectedId);
      } else if (selectedId && key.startsWith("arrow")) {
        event.preventDefault();
        const entry = docRef.current.placements.find((item) => item.id === selectedId);
        if (!entry || entry.mode !== "actual") return;
        const step = event.shiftKey ? 100 : 10;
        const { wall_w_mm: wallW, wall_h_mm: wallH } = docRef.current;
        patchPlacement(selectedId, {
          x_mm: clamp(entry.x_mm + (key === "arrowleft" ? -step : key === "arrowright" ? step : 0), -entry.w_mm, wallW),
          y_mm: clamp(entry.y_mm + (key === "arrowup" ? -step : key === "arrowdown" ? step : 0), -entry.h_mm, wallH),
        });
      } else if (key === "escape") {
        setSelectedId(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const projectItems = () =>
    docRef.current.placements.map((entry) => {
      const details = assets[entry.file_id]?.details || {};
      const sized =
        entry.mode === "actual" && (entry.w_mm !== details.width_mm || entry.h_mm !== details.height_mm)
          ? { overrides: { width_mm: entry.w_mm, height_mm: entry.h_mm } }
          : {};
      return { file_id: entry.file_id, room: doc.title, wall_w_mm: doc.wall_w_mm, wall_h_mm: doc.wall_h_mm, ...sized };
    });

  const addToOwnProject = async () => {
    try {
      await client.post(`/api/projects/${doc.project_id}/items`, { items: projectItems() });
      const name = projects.find((project) => project.id === doc.project_id)?.name || "the project";
      toast.success(
        <span>
          Added to <Link to={`/projects/${doc.project_id}`}>{name}</Link>
        </span>
      );
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not add to the project.");
    }
  };

  const exportWall = async (format) => {
    await flush();
    await downloadFile(`/api/visualisations/${visId}/export`, { format }, `${doc.title}.${format}`);
  };

  // --- render -----------------------------------------------------------------------------

  if (error) {
    return (
      <main className="mb-page ar-page">
        <p className="mb-error">{error}</p>
        <Link to="/visualiser" className="mb-text-button">
          Back to walls
        </Link>
      </main>
    );
  }
  if (!doc) {
    return (
      <main className="mb-page ar-page">
        <p className="mb-muted">Loading wall...</p>
      </main>
    );
  }

  const selected = doc.placements.find((entry) => entry.id === selectedId);
  const hasCover = doc.placements.some((entry) => entry.mode !== "actual");
  const canPlace = Boolean(photo && wallReady && usable && doc.corners);
  const marking = mode === "mark" || !canPlace;

  return (
    <main className="mb-editor ar-wall-editor">
      <ToastContainer position="bottom-right" />
      <header className="mb-toolbar">
        <div className="mb-toolbar-start">
          <Link to="/visualiser" className="mb-icon-button" aria-label="Back to walls" title="Back to walls">
            <ArrowLeft size={18} strokeWidth={1.8} />
          </Link>
          <input
            className="mb-title-input"
            value={doc.title}
            maxLength={120}
            onChange={(event) => patch({ title: event.target.value }, { record: false })}
            onBlur={() => !doc.title.trim() && patch({ title: "Untitled wall" }, { record: false })}
            aria-label="Wall title"
          />
          <span className={`mb-status is-${status === "conflict" ? "error" : status}`} role="status">
            {STATUS[status]}
          </span>
        </div>
        <div className="mb-toolbar-group">
          <button type="button" className="mb-icon-button" onClick={() => travel("past", "future")} disabled={!historyFlags.undo} title="Undo (Ctrl+Z)" aria-label="Undo">
            <Undo2 size={17} strokeWidth={1.8} />
          </button>
          <button type="button" className="mb-icon-button" onClick={() => travel("future", "past")} disabled={!historyFlags.redo} title="Redo (Ctrl+Shift+Z)" aria-label="Redo">
            <Redo2 size={17} strokeWidth={1.8} />
          </button>
          <span className="mb-divider" aria-hidden="true" />
          <button type="button" className="mb-tool" onClick={() => exportWall("jpg")} disabled={!photo}>
            <Download size={16} strokeWidth={1.8} aria-hidden="true" />
            JPG
          </button>
          <button type="button" className="mb-tool" onClick={() => exportWall("png")} disabled={!photo}>
            <Download size={16} strokeWidth={1.8} aria-hidden="true" />
            PNG
          </button>
          <button
            type="button"
            className="mb-primary"
            disabled={!doc.placements.length}
            onClick={() => (doc.project_id ? addToOwnProject() : setDialog("project"))}
          >
            <FolderPlus size={16} strokeWidth={1.8} aria-hidden="true" />
            Add products to project
          </button>
        </div>
      </header>

      {conflict && (
        <div className="mb-banner" role="alert">
          <span>This wall was changed in another tab or window.</span>
          <button type="button" className="mb-text-button" onClick={() => resolveConflict(false)}>
            Load latest
          </button>
          <button type="button" className="mb-primary" onClick={() => resolveConflict(true)}>
            Keep my version
          </button>
        </div>
      )}

      <section className="ar-wall-stage" ref={stageRef} onPointerMove={onPointerMove} onPointerUp={endGesture} onPointerCancel={endGesture}>
        {!photo ? (
          <div className="ar-wall-empty">
            <ImagePlus size={30} strokeWidth={1.4} aria-hidden="true" />
            <h2>Start with a photo of the wall</h2>
            <p>Take it straight on if you can, with the whole wall in view. Then mark its corners and enter its real size.</p>
            <button type="button" className="mb-primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? "Uploading..." : "Choose a photo"}
            </button>
          </div>
        ) : (
          box && (
            <div
              className="ar-wall-box"
              ref={boxRef}
              style={{ width: box.w, height: box.h }}
              onPointerDown={() => !marking && setSelectedId(null)}
            >
              <img className="ar-wall-photo-img" src={absoluteUrl(photo.url) || undefined} alt="" draggable={false} />
              {wallReady && usable && quad && (
                <WallPlane
                  doc={doc}
                  assets={assets}
                  quad={quad}
                  selectedId={selectedId}
                  interactive={!marking}
                  grid={marking ? (units === "m" ? 500 : 304.8) : null}
                  onItemPointerDown={startMove}
                  onResizePointerDown={startResize}
                />
              )}
              {marking && quad && (
                <>
                  <svg className="ar-wall-outline" width={box.w} height={box.h} aria-hidden="true">
                    <polygon points={quad.map((point) => point.join(",")).join(" ")} className={usable ? "" : "is-bad"} />
                  </svg>
                  {quad.map(([x, y], index) => (
                    <span
                      key={index}
                      className="ar-corner"
                      style={{ left: x, top: y }}
                      onPointerDown={(event) => startCorner(event, index)}
                      title={["Top left", "Top right", "Bottom right", "Bottom left"][index]}
                    />
                  ))}
                </>
              )}
            </div>
          )
        )}
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={(event) => uploadPhoto(event.target.files?.[0])} />
      </section>

      <aside className="mb-panel mb-inspector ar-wall-panel" aria-label="Wall settings">
        {marking ? (
          <>
            <h3>Mark the wall</h3>
            <section className="mb-section">
              <p className="ar-small">
                Drag the four dots to the corners of the wall (or the part you can see). The grid shows{" "}
                {units === "m" ? "½ m" : "1 ft"} squares, so it should look square on the wall when the corners and size are right.
              </p>
              {!usable && <p className="ar-warning ar-small">Those corners cross over; drag each dot to its own corner.</p>}
              <div className="ar-field">
                <span className="ar-field-label">Units</span>
                <UnitToggle units={units} onChange={setUnits} />
              </div>
              <div className="ar-row">
                <LengthInput label="Wall width" value={doc.wall_w_mm} units={units} onChange={(mm) => patch({ wall_w_mm: mm })} required />
                <LengthInput label="Wall height" value={doc.wall_h_mm} units={units} onChange={(mm) => patch({ wall_h_mm: mm })} required />
              </div>
              <button type="button" className="mb-primary" disabled={!canPlace} onClick={() => setMode("place")}>
                Done
              </button>
              {photo && (
                <button type="button" className="mb-text-button" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  {uploading ? "Uploading..." : "Use a different photo"}
                </button>
              )}
            </section>
          </>
        ) : (
          <>
            <h3>Wall · {formatSize(doc.wall_w_mm, doc.wall_h_mm, units)}</h3>
            <section className="mb-section">
              <button type="button" className="mb-wide-button" onClick={() => setMode("mark")}>
                <ScanLine size={15} strokeWidth={1.8} aria-hidden="true" />
                Adjust corners or size
              </button>
              <label className="ar-field">
                <span className="ar-field-label">Project</span>
                <select value={doc.project_id || ""} onChange={(event) => patch({ project_id: event.target.value || null })}>
                  <option value="">Not in a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </label>
              {hasCover && (
                <label className="ar-field">
                  <span className="ar-field-label">Room light on wallpaper · {doc.lighting}%</span>
                  <input type="range" min={0} max={100} value={doc.lighting} onChange={(event) => patch({ lighting: Number(event.target.value) }, { record: false })} />
                </label>
              )}
            </section>

            <section className="mb-section">
              <div className="ar-card-head">
                <h4 className="mb-section-title">Products</h4>
                <button type="button" className="mb-tool" onClick={() => setDialog("products")}>
                  <PackageSearch size={15} strokeWidth={1.8} aria-hidden="true" />
                  Add
                </button>
              </div>
              {!doc.placements.length ? (
                <p className="ar-muted ar-small">Add artwork, wallpaper or murals; they appear at their real size.</p>
              ) : (
                <ul className="ar-placements">
                  {[...doc.placements].reverse().map((entry) => (
                    <li key={entry.id}>
                      <button type="button" className={entry.id === selectedId ? "active" : ""} onClick={() => setSelectedId(entry.id)}>
                        {assets[entry.file_id] && <img src={absoluteUrl(assets[entry.file_id].url) || undefined} alt="" />}
                        <span>
                          <strong>{productName(assets[entry.file_id] || {})}</strong>
                          <small>
                            {entry.mode === "actual"
                              ? formatSize(entry.w_mm, entry.h_mm, units)
                              : MODES.find((m) => m.key === entry.mode).label}
                          </small>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            {selected && (
              <section className="mb-section">
                <h4 className="mb-section-title">Selected</h4>
                <div className="ar-segmented ar-segmented-wrap" role="radiogroup" aria-label="How it covers the wall">
                  {MODES.map((entry) => (
                    <button
                      key={entry.key}
                      type="button"
                      role="radio"
                      aria-checked={selected.mode === entry.key}
                      className={selected.mode === entry.key ? "active" : ""}
                      onClick={() => patchPlacement(selected.id, { mode: entry.key })}
                    >
                      {entry.label}
                    </button>
                  ))}
                </div>
                {selected.mode === "actual" && (
                  <div className="ar-row">
                    <LengthInput
                      label="Width"
                      value={selected.w_mm}
                      units={units}
                      onChange={(mm) => mm && patchPlacement(selected.id, { w_mm: Math.max(MIN_MM, mm), h_mm: Math.max(MIN_MM, Math.round((mm * selected.h_mm) / selected.w_mm)) })}
                    />
                    <LengthInput
                      label="Height"
                      value={selected.h_mm}
                      units={units}
                      onChange={(mm) => mm && patchPlacement(selected.id, { h_mm: Math.max(MIN_MM, mm), w_mm: Math.max(MIN_MM, Math.round((mm * selected.w_mm) / selected.h_mm)) })}
                    />
                  </div>
                )}
                {selected.mode === "tile" && (
                  <LengthInput label="Pattern width" value={selected.tile_mm} units={units} onChange={(mm) => mm && patchPlacement(selected.id, { tile_mm: mm })} />
                )}
                {selected.mode === "actual" && (
                  <>
                    <label className="mb-toggle">
                      <input type="checkbox" checked={selected.shadow} onChange={(event) => patchPlacement(selected.id, { shadow: event.target.checked })} />
                      <span>Shadow on the wall</span>
                    </label>
                    <p className="ar-muted ar-small">Drag it on the photo; drag the corner dot to resize. Arrow keys nudge.</p>
                  </>
                )}
                <div className="mb-arrange">
                  <button type="button" onClick={() => reorder(selected.id, true)} title="Bring to front">
                    <BringToFront size={15} strokeWidth={1.8} />
                    <span>Front</span>
                  </button>
                  <button type="button" onClick={() => reorder(selected.id, false)} title="Send to back">
                    <SendToBack size={15} strokeWidth={1.8} />
                    <span>Back</span>
                  </button>
                  <button type="button" className="danger" onClick={() => removePlacement(selected.id)} title="Remove (Del)">
                    <Trash2 size={15} strokeWidth={1.8} />
                    <span>Remove</span>
                  </button>
                </div>
              </section>
            )}
          </>
        )}
      </aside>

      {dialog === "products" && <ProductPicker title="Add a product to the wall" onClose={() => setDialog(null)} onPick={addProducts} />}
      {dialog === "project" && (
        <AddToProjectDialog
          items={projectItems()}
          defaultRoom={doc.title}
          onClose={() => setDialog(null)}
          onAdded={(project) => {
            setProjects((current) => (current.some((p) => p.id === project.id) ? current : [project, ...current]));
            patch({ project_id: project.id }, { record: false });
          }}
        />
      )}
    </main>
  );
}
